import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { Paginator } from "primereact/paginator";
import * as Yup from "yup";
import {
  getLoanList,
  postLoanAddUpdate,
  postLoanDelete,
} from "./Services/PayrollService";
import { getEmployeeList } from "../EmployeeList/Service/employeeService";
import { GetMasterTypeData } from "../../../Services/Global-Services/GlobalServices";
import { TostMsg } from "../../../App";

// ---------- helpers ----------
const rup = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : "₹" + Number(n).toLocaleString("en-IN");

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Keep the date exactly as stored (avoids the UTC off-by-one-day bug of toISOString)
const toInputDate = (d) => (d ? String(d).slice(0, 10) : "");

// API wants the numeric employee id; prefer `EmpId`, fall back to `Id`.
const empKey = (e) => e?.EmpId ?? e?.Id;
const findEmployee = (list, ref) =>
  list.find(
    (e) =>
      String(empKey(e)) === String(ref) ||
      String(e.Id) === String(ref) ||
      String(e.EmploymentId) === String(ref),
  );

const isActive = (l) => String(l.LoanStatusName).toLowerCase() === "active";
const isClosed = (l) => String(l.LoanStatusName).toLowerCase() === "closed";
const balanceOf = (l) =>
  Math.max(0, (l.LoanAmount || 0) - (l.Repaid || 0));
const remainingEmis = (l) =>
  l.MonthlyEMI ? Math.ceil(balanceOf(l) / l.MonthlyEMI) : 0;
const percentRepaid = (l) =>
  l.LoanAmount
    ? Math.min(100, Math.round(((l.Repaid || 0) / l.LoanAmount) * 100))
    : 0;

const ACTIVE_STATUS_DEFAULT = ""; // set from master data once loaded

const EMPTY_FORM = {
  EmpId: "",
  LoanTypeId: "",
  LoanAmount: "",
  DisbursementDate: "",
  Tenure: "",
  MonthlyEMI: "",
  LoanStatusId: ACTIVE_STATUS_DEFAULT,
  FirstDeduction: "",
  FinanceReferenceNumber: "",
  Notes: "",
};

const FieldError = ({ message }) =>
  message ? (
    <div className="text-danger" style={{ fontSize: "12px", marginTop: "4px" }}>
      {message}
    </div>
  ) : null;

const LoanManagement = () => {
  const toaster = useContext(TostMsg);
  const userDetails = useMemo(
    () => JSON.parse(localStorage.getItem("User") || "{}"),
    [],
  );

  // master / lookup data
  const [loanStatusList, setLoanStatusList] = useState([]);
  const [loanTypesList, setLoanTypesList] = useState([]);
  const [formonthList, setFormonthList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);

  // list data
  const [loanSummary, setLoanSummary] = useState(null);
  const [loans, setLoans] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  // filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const searchTimeout = useRef(null);

  // modals
  const [showFormModal, setShowFormModal] = useState(false);
  const [showStmtModal, setShowStmtModal] = useState(false);
  const [showDelModal, setShowDelModal] = useState(false);
  const [editLoan, setEditLoan] = useState(null); // full loan being edited
  const [deleteLoan, setDeleteLoan] = useState(null);
  const [stmtData, setStmtData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // form
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [emiEdited, setEmiEdited] = useState(false);

  // ---------- validation ----------
  const loanValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        EmpId: Yup.string().required("Employee is required"),
        LoanTypeId: Yup.string().required("Loan type is required"),
        LoanAmount: Yup.number()
          .typeError("Enter a valid amount")
          .required("Amount is required")
          .min(1, "Amount must be at least ₹1")
          .max(10000000, "Amount cannot exceed ₹1,00,00,000")
          .test(
            "not-below-repaid",
            "Amount cannot be less than what is already repaid",
            function (value) {
              const repaid = this.options.context?.repaid || 0;
              return value === undefined || value >= repaid;
            },
          ),
        DisbursementDate: Yup.date()
          .typeError("Select a valid date")
          .required("Disbursement date is required"),
        Tenure: Yup.number()
          .typeError("Enter tenure in months")
          .required("Tenure is required")
          .integer("Tenure must be a whole number of months")
          .min(1, "Tenure must be at least 1 month")
          .max(120, "Tenure cannot exceed 120 months"),
        MonthlyEMI: Yup.number()
          .typeError("Enter a valid EMI")
          .required("EMI is required")
          .min(1, "EMI must be at least ₹1")
          .max(1000000, "EMI cannot exceed ₹10,00,000")
          .test(
            "emi-le-amount",
            "EMI cannot be more than the loan amount",
            function (value) {
              const amount = Number(this.parent.LoanAmount);
              return !amount || !value || value <= amount;
            },
          ),
        LoanStatusId: Yup.string().required("Status is required"),
        FirstDeduction: Yup.string(),
        FinanceReferenceNumber: Yup.string()
          .max(50, "Reference number cannot exceed 50 characters")
          .matches(/^[A-Za-z0-9\-_/]*$/, {
            message: "Use only letters, numbers, - _ /",
            excludeEmptyString: true,
          }),
        Notes: Yup.string().max(500, "Notes cannot exceed 500 characters"),
      }),
    [],
  );

  const validationContext = { repaid: editLoan?.Repaid || 0 };

  const validateField = async (name, data = formData) => {
    try {
      await loanValidationSchema.validateAt(name, data, {
        context: validationContext,
      });
      setFormErrors((p) => ({ ...p, [name]: "" }));
    } catch (err) {
      setFormErrors((p) => ({ ...p, [name]: err.message }));
    }
  };

  const validateForm = async () => {
    try {
      await loanValidationSchema.validate(formData, {
        abortEarly: false,
        context: validationContext,
      });
      setFormErrors({});
      return true;
    } catch (err) {
      const errors = {};
      err.inner.forEach((e) => {
        if (!errors[e.path]) errors[e.path] = e.message;
      });
      setFormErrors(errors);
      return false;
    }
  };

  // ---------- data loading ----------
  useEffect(() => {
    loadMasterData();
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMasterData = async () => {
    try {
      const base = { allRecords: true, organisationId: userDetails?.OrgId };
      const [statusRes, typeRes, monthRes] = await Promise.all([
        GetMasterTypeData({ ...base, masterType: "LoanStatus" }),
        GetMasterTypeData({ ...base, masterType: "LoanTypes" }),
        GetMasterTypeData({ ...base, masterType: "formonth" }),
      ]);
      setLoanStatusList(statusRes?.Data || []);
      setLoanTypesList(typeRes?.Data || []);
      setFormonthList(monthRes?.Data || []);
    } catch (error) {
      console.error("Error loading master data:", error);
      toaster?.current?.error("Could not load loan master data.");
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await getEmployeeList({
        OrganisationId: userDetails?.OrgId,
        LoggedInEmployeeId: userDetails?.EpmId,
        IsDirectReportOrHierarchy: 0,
        EmployeeId: 0,
        SearchText: "",
        FollowersOnly: 0,
        FollowingsOnly: 0,
        IsSuggestedOnly: 0,
        IsExEmployeesAlso: 0,
        pageNumber: 1,
        pageSize: 10000,
      });
      setEmployeeList(res?.Data || []);
    } catch (error) {
      console.error("Error fetching employee list:", error);
      toaster?.current?.error("Could not load employee list.");
    }
  };

  const fetchLoans = async (page = 1, size = rows) => {
    setIsLoading(true);
    try {
      const res = await getLoanList({
        LoanId: 0,
        LoggedInEmpId: 0,
        OrgId: userDetails?.OrgId,
        GUID: "",
        EmpId: 0,
        PageNumber: page,
        PageSize: size,
        Search: searchQuery || "",
        LoanStatusId: filterStatus || "",
        LoanTypeId: filterType || "",
      });
      const items = res?.Data?.LoanItems || [];
      setLoanSummary(res?.Data?.LoanSummary || null);
      setLoans(items);
      setTotalRecords(
        res?.Data?.RecordCount ??
          res?.Data?.TotalRecords ??
          res?.Data?.TotalCount ??
          items.length,
      );
    } catch (error) {
      console.error("Error fetching loan list:", error);
      toaster?.current?.error("Could not load loans. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // debounced reload on search / filters (also runs on first mount)
  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFirst(0);
      fetchLoans(1, rows);
    }, 500);
    return () => clearTimeout(searchTimeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterType, filterStatus]);

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
    fetchLoans(event.first / event.rows + 1, event.rows);
  };

  const currentPage = () => Math.floor(first / rows) + 1;

  // ---------- form handlers ----------
  const defaultStatusId = () => {
    const active = loanStatusList.find(
      (s) => String(s.Name).toLowerCase() === "active",
    );
    return active ? String(active.Id) : "";
  };

  const openAdd = () => {
    setEditLoan(null);
    setEmiEdited(false);
    setFormErrors({});
    setFormData({ ...EMPTY_FORM, LoanStatusId: defaultStatusId() });
    setShowFormModal(true);
  };

  const openEdit = (loan) => {
    setEditLoan(loan);
    setEmiEdited(true); // keep the saved EMI; don't overwrite it automatically
    setFormErrors({});
    setFormData({
      EmpId: (() => {
        const emp = findEmployee(employeeList, loan.EmpId);
        return emp ? String(empKey(emp)) : "";
      })(),
      LoanTypeId: String(loan.LoanTypeId ?? ""),
      LoanAmount: loan.LoanAmount ?? "",
      DisbursementDate: toInputDate(loan.DisbursedOn),
      Tenure: loan.Tenure ?? "",
      MonthlyEMI: loan.MonthlyEMI ?? "",
      LoanStatusId: String(loan.LoanStatusId ?? ""),
      FirstDeduction: loan.FirstDeduction || "",
      FinanceReferenceNumber: loan.FinanceReferenceNumber || "",
      Notes: loan.Notes || "",
    });
    setShowFormModal(true);
  };

  const closeForm = () => {
    if (isSaving) return;
    setShowFormModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...formData, [name]: value };

    // auto-calculate EMI until the user types their own value
    if (name === "MonthlyEMI") {
      setEmiEdited(value !== "");
    } else if (
      (name === "LoanAmount" || name === "Tenure") &&
      !emiEdited
    ) {
      const amt = parseFloat(next.LoanAmount);
      const ten = parseInt(next.Tenure, 10);
      next.MonthlyEMI = amt > 0 && ten > 0 ? Math.ceil(amt / ten) : "";
    }

    setFormData(next);
    // re-validate fields that were already flagged (or depend on this one)
    const toCheck = new Set([name]);
    if (name === "LoanAmount" || name === "Tenure") toCheck.add("MonthlyEMI");
    toCheck.forEach((f) => {
      if (formErrors[f] || f === name) validateField(f, next);
    });
  };

  const handleBlur = (e) => validateField(e.target.name);

  // Show server-side validation errors under the right field when the API provides them
  const applyServerErrors = (res) => {
    const serverErrors = res?.Errors || res?.errors;
    if (!serverErrors || typeof serverErrors !== "object") return false;
    const mapped = {};
    const entries = Array.isArray(serverErrors)
      ? serverErrors.map((e) => [e.Field || e.field || e.PropertyName, e.Message || e.message])
      : Object.entries(serverErrors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]);
    entries.forEach(([key, msg]) => {
      if (!key || !msg) return;
      const field = Object.keys(EMPTY_FORM).find(
        (f) => f.toLowerCase() === String(key).toLowerCase(),
      );
      if (field) mapped[field] = msg;
    });
    if (Object.keys(mapped).length) {
      setFormErrors((p) => ({ ...p, ...mapped }));
      return true;
    }
    return false;
  };

  // ---------- CRUD ----------
  const saveLoan = async () => {
    if (isSaving) return;
    if (!(await validateForm())) return;

    const f = formData;
    const params = {
      Id: editLoan?.Id || 0,
      OrgId: userDetails?.OrgId,
      EmpId: Number(f.EmpId),
      LoanTypeId: Number(f.LoanTypeId),
      LoanStatusId: Number(f.LoanStatusId),
      FirstDeduction: f.FirstDeduction,
      LoanAmount: Number(f.LoanAmount),
      PaidAmount: editLoan?.Repaid || 0, // never reset repayments on edit
      DisbursementDate: f.DisbursementDate,
      Tenure: Number(f.Tenure),
      MonthlyEMI: Number(f.MonthlyEMI),
      FinanceReferenceNumber: f.FinanceReferenceNumber.trim(),
      Notes: f.Notes.trim(),
    };

    setIsSaving(true);
    try {
      const res = await postLoanAddUpdate(params);
      if (res?.StatusCode === 2000) {
        toaster?.current?.success(
          `Loan ${editLoan ? "updated" : "added"} successfully`,
        );
        setShowFormModal(false);
        if (editLoan) {
          fetchLoans(currentPage(), rows);
        } else {
          setFirst(0);
          fetchLoans(1, rows);
        }
      } else {
        const shown = applyServerErrors(res);
        toaster?.current?.error(
          res?.Message ||
            (shown ? "Please fix the highlighted fields." : "Error saving loan. Please try again."),
        );
      }
    } catch (error) {
      console.error("Error saving loan:", error);
      toaster?.current?.error("Error saving loan. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const askDelete = (loan) => {
    setDeleteLoan(loan);
    setShowDelModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteLoan || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await postLoanDelete({ RequestId: deleteLoan.Id });
      if (res?.StatusCode === 2000) {
        toaster?.current?.success("Loan deleted successfully");
        setShowDelModal(false);
        setDeleteLoan(null);
        // if we just removed the last row of a page, go back one page
        let page = currentPage();
        if (loans.length === 1 && page > 1) {
          page -= 1;
          setFirst((page - 1) * rows);
        }
        fetchLoans(page, rows);
      } else {
        toaster?.current?.error(
          res?.Message || "Error deleting loan. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error deleting loan:", error);
      toaster?.current?.error("Error deleting loan. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ---------- statement ----------
  const viewStmt = (loan) => {
    const emi = loan.MonthlyEMI || 0;
    const paidEmis = emi ? Math.floor((loan.Repaid || 0) / emi) : 0;
    const start = new Date(loan.DisbursedOn);
    const list = [];
    let opening = loan.LoanAmount || 0;
    for (let i = 1; i <= (loan.Tenure || 0); i++) {
      const d = new Date(start);
      d.setMonth(d.getMonth() + i);
      const deduction = Math.min(emi, opening);
      const closing = Math.max(0, opening - deduction);
      list.push({
        no: i,
        month: d.toLocaleDateString("en-IN", { month: "short", year: "numeric" }),
        opening,
        emi: deduction,
        closing,
        status: i <= paidEmis ? "Paid" : i === paidEmis + 1 ? "Current" : "Upcoming",
      });
      opening = closing;
    }
    setStmtData({ loan, rows: list, percent: percentRepaid(loan), paidEmis });
    setShowStmtModal(true);
  };

  // ---------- export (current page) ----------
  const handleExport = () => {
    if (!loans.length) {
      toaster?.current?.error("No data to export.");
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = [
      "Employee", "Employee ID", "Type", "Amount", "Disbursed On",
      "Tenure", "EMI", "Repaid", "Balance", "Status",
    ];
    const body = loans.map((l) => [
      l.EmployeeName, l.EmpId, l.LoanTypeName, l.LoanAmount,
      toInputDate(l.DisbursedOn), l.Tenure, l.MonthlyEMI, l.Repaid,
      balanceOf(l), l.LoanStatusName,
    ]);
    const csv = [headers, ...body].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "loan_data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---------- page totals ----------
  const pageTotals = useMemo(() => {
    const active = loans.filter(isActive);
    return {
      amount: loans.reduce((a, l) => a + (l.LoanAmount || 0), 0),
      repaid: loans.reduce((a, l) => a + (l.Repaid || 0), 0),
      balance: loans.reduce((a, l) => a + balanceOf(l), 0),
      emi: active.reduce((a, l) => a + (l.MonthlyEMI || 0), 0),
    };
  }, [loans]);

  const amountNum = parseFloat(formData.LoanAmount);
  const emiNum = parseFloat(formData.MonthlyEMI);
  const tenureNum = parseInt(formData.Tenure, 10);
  const showPreview = amountNum > 0 && tenureNum > 0 && emiNum > 0;

  // ---------- render ----------
  return (
    <div className="container-fluid">
      <div className="form-head d-flex flex-wrap align-items-center page-head mb-0">
        <div className="me-auto">
          <h2 className="font-w600 mb-0">Loan Management</h2>
          <p>
            Only approved loans added here flow into payroll processing (Step 3
            of Run Payroll)
          </p>
        </div>
        <div className="rightside-tabs">
          <div className="topbar-actions">
            <button className="btn btn-light btn-sm" onClick={handleExport}>
              <i className="fa-solid fa-file-excel"></i> Export
            </button>
            <button className="btn btn-brand btn-sm" onClick={openAdd}>
              <i className="fa-solid fa-plus"></i> Add Loan
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* SUMMARY */}
        <div className="loan-summary-grid">
          {[
            ["Active Loans", loanSummary?.ActiveLoans ?? 0, false],
            ["Total Disbursed", loanSummary?.TotalDisbursed ?? 0, true],
            ["Total Repaid", loanSummary?.TotalRepaid ?? 0, true],
            ["Outstanding", loanSummary?.Outstanding ?? 0, true],
            ["This Month EMI Total", loanSummary?.ThisMonthEMITotal ?? 0, true],
          ].map(([label, value, money]) => (
            <div className="loan-summary-card" key={label}>
              <div className="loan-summary-label">{label}</div>
              <div className="loan-summary-value">
                {money ? rup(value) : value}
              </div>
            </div>
          ))}
        </div>

        {/* TABLE */}
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="toolbar filter-row pt-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search employee…"
              style={{ width: "200px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="toolbar-sep"></div>
            <select
              className="fs-select"
              style={{ width: "140px" }}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {loanTypesList.map((t) => (
                <option key={t.Id} value={t.Id}>{t.Name}</option>
              ))}
            </select>
            <select
              className="fs-select"
              style={{ width: "120px" }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              {loanStatusList.map((s) => (
                <option key={s.Id} value={s.Id}>{s.Name}</option>
              ))}
            </select>
            <div className="toolbar-spacer"></div>
            <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
              {totalRecords} records
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="mgmt-tbl">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th className="tr">Loan Amount</th>
                  <th className="tr">Disbursed On</th>
                  <th className="tr">Tenure</th>
                  <th className="tr">Monthly EMI</th>
                  <th className="tr">Repaid</th>
                  <th className="tr">Balance</th>
                  <th className="tr">Rem. EMIs</th>
                  <th>Progress</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                  <th style={{ textAlign: "center" }} className="no-print">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan="12" style={{ textAlign: "center", padding: "24px" }}>
                      Loading loans…
                    </td>
                  </tr>
                )}
                {!isLoading && loans.length === 0 && (
                  <tr>
                    <td colSpan="12" style={{ textAlign: "center", padding: "24px" }}>
                      No loans found. Change the filters or add a loan.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  loans.map((l) => {
                    const b = balanceOf(l);
                    const r = remainingEmis(l);
                    const p = percentRepaid(l);
                    const badge = isActive(l)
                      ? "badge-active"
                      : isClosed(l)
                        ? "badge-paid"
                        : "badge-locked";
                    const barColor = isClosed(l) || p > 75
                      ? "var(--success)"
                      : p > 40
                        ? "var(--warning)"
                        : "var(--brand)";
                    return (
                      <tr key={l.Id}>
                        <td>
                          <span style={{ fontWeight: 600 }}>{l.EmployeeName}</span>
                          <br />
                          <code style={{ fontSize: "10px", color: "var(--brand)" }}>
                            {l.EmployeeCode || l.EmpId}
                          </code>
                        </td>
                        <td>
                          <span className="badge badge-account" style={{ fontSize: "10.5px" }}>
                            {l.LoanTypeName}
                          </span>
                        </td>
                        <td className="tr">{rup(l.LoanAmount)}</td>
                        <td className="tr" style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
                          {fmtDate(l.DisbursedOn)}
                        </td>
                        <td className="tr">{l.Tenure} mo.</td>
                        <td className="tr">{rup(l.MonthlyEMI)}</td>
                        <td className="tr" style={{ color: "var(--success)" }}>{rup(l.Repaid || 0)}</td>
                        <td className="tr" style={{ fontWeight: 700, color: b > 0 ? "#92400e" : "var(--success)" }}>
                          {b > 0 ? rup(b) : <span style={{ color: "var(--success)" }}>Nil</span>}
                        </td>
                        <td className="tr">
                          {r > 0 ? `${r} EMIs` : <span style={{ color: "var(--success)" }}>Done</span>}
                        </td>
                        <td style={{ minWidth: "100px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div className="pbar-wrap" style={{ flex: 1 }}>
                              <div className="pbar-fill" style={{ width: `${p}%`, background: barColor }}></div>
                            </div>
                            <span style={{ fontSize: "10.5px", fontWeight: 700, color: barColor }}>{p}%</span>
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span className={`badge ${badge}`}>{l.LoanStatusName}</span>
                        </td>
                        <td style={{ textAlign: "center" }} className="no-print">
                          <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                            <button className="btn btn-light btn-sm" onClick={() => viewStmt(l)} title="Repayment Statement">
                              <i className="fa-solid fa-file-lines"></i>
                            </button>
                            <button className="btn btn-light btn-sm" onClick={() => openEdit(l)} title="Edit">
                              <i className="fa-solid fa-pen"></i>
                            </button>
                            <button className="btn btn-light btn-sm" onClick={() => askDelete(l)} title="Delete" style={{ color: "var(--danger)" }}>
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="2">PAGE TOTAL</td>
                  <td className="tr">{rup(pageTotals.amount)}</td>
                  <td>—</td>
                  <td>—</td>
                  <td className="tr">{rup(pageTotals.emi)}</td>
                  <td className="tr" style={{ color: "var(--success)" }}>{rup(pageTotals.repaid)}</td>
                  <td className="tr" style={{ color: "var(--warning)" }}>{rup(pageTotals.balance)}</td>
                  <td colSpan="4">—</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Paginator
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={onPageChange}
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          />
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      {showFormModal && (
        <div className="mo open" onClick={closeForm}>
          <div className="mb" onClick={(e) => e.stopPropagation()}>
            <div className="mh">
              <div className="icon-div">
                <i className="fa-solid fa-hand-holding-dollar" style={{ fontSize: "13px" }}></i>
              </div>
              <h6>{editLoan ? "Edit Loan Record" : "Add Loan / Advance"}</h6>
              <button
                style={{ marginLeft: "auto", background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#888", lineHeight: 1 }}
                onClick={closeForm}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="mbody">
              <div className="fsec">Employee &amp; Loan Details</div>
              <div className="fg2">
                <div>
                  <label className="form-label">
                    Employee <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="EmpId"
                    value={formData.EmpId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={!!editLoan && !!formData.EmpId}
                  >
                    <option value="">Select…</option>
                    {employeeList.map((emp) => (
                      <option key={emp.Id} value={empKey(emp)}>
                        {emp.FullName} ({emp.EmploymentId})
                      </option>
                    ))}
                  </select>
                  <FieldError message={formErrors.EmpId} />
                </div>
                <div>
                  <label className="form-label">
                    Loan Type <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="LoanTypeId"
                    value={formData.LoanTypeId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Select…</option>
                    {loanTypesList.map((t) => (
                      <option key={t.Id} value={t.Id}>{t.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.LoanTypeId} />
                </div>
                <div>
                  <label className="form-label">
                    Loan Amount (₹) <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control w-100"
                    name="LoanAmount"
                    placeholder="0"
                    min="1"
                    max="10000000"
                    value={formData.LoanAmount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.LoanAmount} />
                </div>
                <div>
                  <label className="form-label">
                    Disbursement Date <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control w-100"
                    name="DisbursementDate"
                    value={formData.DisbursementDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.DisbursementDate} />
                </div>
              </div>

              <div className="fsec">Repayment Terms</div>
              <div className="fg3">
                <div>
                  <label className="form-label">
                    Tenure (months) <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control w-100"
                    name="Tenure"
                    min="1"
                    max="120"
                    placeholder="12"
                    value={formData.Tenure}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.Tenure} />
                </div>
                <div>
                  <label className="form-label">
                    Monthly EMI (₹) <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control w-100"
                    name="MonthlyEMI"
                    placeholder="Auto"
                    min="1"
                    max="1000000"
                    value={formData.MonthlyEMI}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.MonthlyEMI} />
                </div>
                <div>
                  <label className="form-label">First Deduction</label>
                  <select
                    className="form-select w-100"
                    name="FirstDeduction"
                    value={formData.FirstDeduction}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {formonthList.map((m) => (
                      <option key={m.Id} value={m.Name}>{m.Name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="fsec">Status &amp; Notes</div>
              <div className="fg2">
                <div>
                  <label className="form-label">
                    Status <span style={{ color: "var(--danger)" }}>*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    name="LoanStatusId"
                    value={formData.LoanStatusId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Select…</option>
                    {loanStatusList.map((s) => (
                      <option key={s.Id} value={s.Id}>{s.Name}</option>
                    ))}
                  </select>
                  <FieldError message={formErrors.LoanStatusId} />
                </div>
                <div>
                  <label className="form-label">Finance Reference No.</label>
                  <input
                    type="text"
                    className="form-control w-100"
                    name="FinanceReferenceNumber"
                    placeholder="FIN-2025-XXX"
                    maxLength="50"
                    value={formData.FinanceReferenceNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.FinanceReferenceNumber} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control w-100"
                    name="Notes"
                    rows="2"
                    placeholder="Additional notes…"
                    maxLength="500"
                    style={{ resize: "vertical" }}
                    value={formData.Notes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <FieldError message={formErrors.Notes} />
                </div>
              </div>

              {showPreview && (
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "10px 14px", marginTop: "14px" }}>
                  <div style={{ fontSize: "10.5px", fontWeight: 700, color: "#166534", textTransform: "uppercase", letterSpacing: ".4px", marginBottom: "6px" }}>
                    Repayment Preview
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", fontSize: "12.5px" }}>
                    <div><span style={{ color: "var(--text-muted)" }}>Loan:</span> <strong>{rup(amountNum)}</strong></div>
                    <div><span style={{ color: "var(--text-muted)" }}>Monthly EMI:</span> <strong>{rup(emiNum)}</strong></div>
                    <div><span style={{ color: "var(--text-muted)" }}>Tenure:</span> <strong>{tenureNum} months</strong></div>
                    <div><span style={{ color: "var(--text-muted)" }}>Total Payable:</span> <strong>{rup(emiNum * tenureNum)}</strong></div>
                  </div>
                </div>
              )}
            </div>

            <div className="mfoot">
              <button className="btn btn-light" onClick={closeForm} disabled={isSaving}>
                Cancel
              </button>
              <button className="btn btn-brand" onClick={saveLoan} disabled={isSaving}>
                <i className="fa-solid fa-check"></i>{" "}
                {isSaving ? "Saving…" : editLoan ? "Save Changes" : "Add Loan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STATEMENT MODAL */}
      {showStmtModal && stmtData && (
        <div className="mo open" onClick={() => setShowStmtModal(false)}>
          <div className="mb wide" onClick={(e) => e.stopPropagation()}>
            <div className="mh no-print">
              <h6>Loan Repayment Statement</h6>
              <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
                <button className="btn btn-light btn-sm" onClick={() => window.print()}>
                  <i className="fa-solid fa-print"></i> Print
                </button>
                <button className="btn btn-light btn-sm" onClick={() => setShowStmtModal(false)}>
                  Close
                </button>
              </div>
            </div>
            <div style={{ padding: "24px" }}>
              <div style={{ display: "flex", gap: "16px", marginBottom: "20px", paddingBottom: "16px", borderBottom: "2px solid var(--border)", flexWrap: "wrap" }}>
                {[
                  ["Employee", stmtData.loan.EmployeeName],
                  ["Loan Type", stmtData.loan.LoanTypeName],
                  ["Loan Amount", rup(stmtData.loan.LoanAmount)],
                  ["Disbursed On", fmtDate(stmtData.loan.DisbursedOn)],
                  ["Monthly EMI", rup(stmtData.loan.MonthlyEMI)],
                  ["Repaid", `${rup(stmtData.loan.Repaid || 0)} (${stmtData.percent}%)`],
                  ["Balance", rup(balanceOf(stmtData.loan))],
                ].map(([label, value]) => (
                  <div key={label} style={{ flex: 1, minWidth: "130px" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--text-muted)", marginBottom: "3px" }}>{label}</div>
                    <div style={{ fontSize: "14px", fontWeight: 700 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="mgmt-tbl">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Month</th>
                      <th className="tr">Opening Balance</th>
                      <th className="tr">EMI</th>
                      <th className="tr">Closing Balance</th>
                      <th style={{ textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stmtData.rows.map((r) => (
                      <tr key={r.no}>
                        <td>{r.no}</td>
                        <td>{r.month}</td>
                        <td className="tr">{rup(r.opening)}</td>
                        <td className="tr">{rup(r.emi)}</td>
                        <td className="tr">{r.closing > 0 ? rup(r.closing) : "Nil"}</td>
                        <td style={{ textAlign: "center" }}>
                          <span className={`badge ${r.status === "Paid" ? "badge-paid" : r.status === "Current" ? "badge-active" : "badge-locked"}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "12px" }}>
                Schedule is calculated from the disbursement date, tenure and EMI.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {showDelModal && deleteLoan && (
        <div className="mo open" onClick={() => !isDeleting && setShowDelModal(false)}>
          <div
            style={{ background: "#fff", borderRadius: "12px", width: "400px", padding: "24px", boxShadow: "0 24px 64px rgba(0,0,0,.18)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
              <div style={{ width: "34px", height: "34px", background: "#fee2e2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--danger)" }}>
                <i className="fa-solid fa-trash"></i>
              </div>
              <h6 style={{ margin: 0, fontWeight: 700 }}>Delete Loan Record</h6>
            </div>
            <p style={{ fontSize: "13px", color: "var(--text-mid)", margin: "0 0 18px" }}>
              Delete the {deleteLoan.LoanTypeName} of {rup(deleteLoan.LoanAmount)} for{" "}
              {deleteLoan.EmployeeName}? This will remove it from payroll deductions.
            </p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button className="btn btn-light" onClick={() => setShowDelModal(false)} disabled={isDeleting}>
                Cancel
              </button>
              <button
                className="btn btn-brand"
                style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanManagement;
